import { useState } from 'react';
import { useSelector } from 'react-redux';
import { RootState } from '../../store';
import { serviceMappingEndpoints } from '../../services/api/endpoints/serviceMapping.endpoint';
import { ServiceResourceMapping } from '../../types/service';
import { logger } from '../../utils/logger';

export const useServiceMapping = () => {
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const selectedOrganizationId = useSelector(
    (state: RootState) => state.organizationContext.selectedOrganizationId
  );

  const createMapping = async (
    serviceId: number,
    resourceId: number
  ): Promise<ServiceResourceMapping | null> => {
    if (!selectedOrganizationId) {
      setError('No organization selected');
      return null;
    }

    setIsLoading(true);
    setError(null);

    try {
      return await serviceMappingEndpoints.createMapping(
        selectedOrganizationId,
        serviceId,
        resourceId
      );
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to create service mapping';
      setError(message);
      logger.error('Error creating service mapping:', err);
      return null;
    } finally {
      setIsLoading(false);
    }
  };

  const deleteMapping = async (mappingId: number): Promise<boolean> => {
    if (!selectedOrganizationId) {
      setError('No organization selected');
      return false;
    }

    setIsLoading(true);
    setError(null);

    try {
      await serviceMappingEndpoints.deleteMapping(selectedOrganizationId, mappingId);
      return true;
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to delete service mapping';
      setError(message);
      logger.error('Error deleting service mapping:', err);
      return false;
    } finally {
      setIsLoading(false);
    }
  };

  return {
    createMapping,
    deleteMapping,
    isLoading,
    error,
  };
};
